import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CSSColorEditor } from "./css-color/css-color";
import { SIconButton, SIconType } from "../../components";
import { clamp } from "es-toolkit";
import { sortBy } from "es-toolkit/compat";
export const CSSBackgroundImageEditor = ({ colors, setColors })=>{
    const [_color, setColor] = useState(`linear-gradient(90deg, ${colors.map((c)=>{
        return `${hexToRgba(c.color.value)} ${c.position.value}${c.position.unit}`;
    }).join(",")})`);
    const { mouseDown, offset } = useMouseOffset();
    const [activeIndex, setActiveIndex] = useState(0);
    const [startingPercentage, setStartingPercentage] = useState(undefined);
    const [sliderWidth, setSliderWidth] = useState(0);
    const sliderBackgroundRef = useRef(null);
    useEffect(()=>{
        const newPercentage = clamp((startingPercentage || 0) + Math.round(offset.x / sliderWidth * 10000) / 100, 0, 100);
        console.log("[css-bg-image] newPercentage", newPercentage);
        if (activeIndex !== undefined && isNaN(newPercentage) === false) {
            const newColors = colors.map((c, i)=>{
                if (i === activeIndex) {
                    return {
                        ...c,
                        position: {
                            ...c.position,
                            value: newPercentage.toString()
                        }
                    };
                }
                return c;
            });
            setColors(newColors);
        }
    }, [
        offset
    ]);
    useEffect(()=>{
        if (sliderBackgroundRef.current) {
            setSliderWidth(sliderBackgroundRef.current.offsetWidth);
        }
    }, [
        sliderBackgroundRef.current
    ]);
    const bgGradientCSS = useMemo(()=>{
        const sorted = sortBy(colors, (c)=>parseFloat(c.position.value));
        return `linear-gradient(90deg, ${sorted.map((c)=>{
            return `${c.color.value} ${c.position.value}${c.position.unit}`;
        }).join(",")})`;
    }, [
        colors
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid grid-cols-1 gap-2 group/slider",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "h-[32px] grid grid-cols-1 items-center bg-grayscale-dark px-2 border border-white/10 rounded-md",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "h-[8px] rounded rounded-full outline outline-white/20 relative grid grid-cols-[repeat(100,minmax(0,1fr))]",
                    ref: sliderBackgroundRef,
                    style: {
                        backgroundSize: "8px 8px",
                        backgroundPosition: "0 0, 4px 4px",
                        backgroundImage: "linear-gradient(45deg, #C7C7C7 25%, transparent 25%, transparent 75%, #C7C7C7 75%, #C7C7C7), linear-gradient(45deg, #C7C7C7 25%, #ffffff 25%, #ffffff 75%, #C7C7C7 75%, #C7C7C7)"
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                backgroundImage: bgGradientCSS
                            },
                            className: "absolute top-0 bottom-0 left-0 right-0 rounded rounded-full z-0"
                        }),
                        colors.map((c, i)=>{
                            return /*#__PURE__*/ _jsx("div", {
                                onMouseDown: (e)=>{
                                    mouseDown(e);
                                    setActiveIndex(i);
                                    setStartingPercentage(parseFloat(c.position.value));
                                },
                                onMouseUp: ()=>{},
                                style: {
                                    left: `${parseFloat(c.position.value) * ((sliderWidth - 16) / sliderWidth)}%`,
                                    backgroundColor: c.color.value
                                },
                                className: `z-[2] cursor-pointer active:scale-[1.0] w-[18px] h-[18px] top-[50%] rounded-full absolute translate-y-[-50%] border border-[2px] border-white/20 backdrop-blur  outline outline-[2px] outline-grayscale-dark/40 hover:scale-[1.1] transition transition-[box-shadow,transform] ${i === activeIndex ? "group-hover/slider:ring group-hover/slider:ring-[2px] group-hover/slider:ring-offset-2 group-hover/slider:ring-offset-grayscale-dark group-hover/slider:ring-sky-500" : ""}`
                            }, i);
                        }),
                        [
                            ...Array(100).keys()
                        ].map((i)=>{
                            return /*#__PURE__*/ _jsx("div", {
                                // on mouse down, create a new color stop here
                                onMouseDown: (e)=>{
                                    const newColors = sortBy([
                                        ...colors
                                    ], (c)=>parseFloat(c.position.value));
                                    // get the new index of the new color
                                    let newIndex = newColors.findIndex((c)=>parseFloat(c.position.value) > i);
                                    if (newIndex === -1) {
                                        newIndex = newColors.length - 1;
                                    }
                                    const newColor = {
                                        color: {
                                            type: "css-color",
                                            value: newColors[newIndex].color.value
                                        },
                                        position: {
                                            type: "css-number-unit",
                                            value: `${i}`,
                                            unit: "%"
                                        }
                                    };
                                    newColors.splice(newIndex, 0, newColor);
                                    setColors(newColors);
                                    setActiveIndex(newIndex);
                                    mouseDown(e);
                                    setStartingPercentage(i);
                                },
                                style: {
                                    left: `${i}%`
                                },
                                className: "h-full hover:bg-white/20 z-[1]"
                            }, i);
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `grid  gap-2 ${colors.length >= 3 ? "grid-cols-[1fr,auto]" : "grid-cols-1"}`,
                children: [
                    activeIndex !== undefined ? /*#__PURE__*/ _jsx(CSSColorEditor, {
                        value: {
                            type: "css-color",
                            value: colors[activeIndex].color.value
                        },
                        onChangeValue: (value)=>{
                            const newColors = colors.map((c, i)=>{
                                if (i === activeIndex && value) {
                                    return {
                                        ...c,
                                        color: value
                                    };
                                }
                                return c;
                            });
                            setColors(newColors);
                        }
                    }, activeIndex) : undefined,
                    colors.length >= 3 && /*#__PURE__*/ _jsx(SIconButton, {
                        icon: SIconType.Trash,
                        iconSize: 14,
                        onClick: ()=>{
                            if (activeIndex !== undefined) {
                                const newColors = colors.filter((_, i)=>i !== activeIndex);
                                setColors(newColors);
                                if (activeIndex >= newColors.length) {
                                    setActiveIndex(newColors.length - 1);
                                } else if (activeIndex > 0) {
                                    setActiveIndex(activeIndex - 1);
                                }
                            }
                        }
                    })
                ]
            })
        ]
    });
};
// converts an 8 digit hex to rgba
const hexToRgba = (_hex)=>{
    const hex = `#${_hex.replace("#", "").replace("0x", "").trim()}`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const a = parseInt(hex.slice(7, 9), 16) / 255;
    return `rgba(${r},${g},${b},${a})`;
};
// converts RGBA to 8 digit hex
const rgbaToHex = (rgba)=>{
    const [r, g, b, a] = rgba.toLowerCase().replace("rgba(", "").replace(")", "").split(",").map((n)=>parseFloat(n));
    const output = `#${[
        r,
        g,
        b,
        Math.round(a * 255)
    ].map((n)=>n.toString(16).padStart(2, "0")).join("")}`;
    return output;
};
const useMouseOffset = ()=>{
    const [offset, setOffset] = useState({
        x: 0,
        y: 0
    });
    const [startPosition, setStartPosition] = useState({
        x: 0,
        y: 0
    });
    const [isTracking, setIsTracking] = useState(false);
    const mouseDown = useCallback((event)=>{
        console.log("[css-bg-image] mouse down");
        setIsTracking(true);
        setStartPosition({
            x: event.clientX,
            y: event.clientY
        });
    }, []);
    const mouseUp = useCallback(()=>{
        console.log("[css-bg-image] mouse up");
        setIsTracking(false);
        setStartPosition({
            x: 0,
            y: 0
        });
    }, []);
    useEffect(()=>{
        const onMouseMove = (event)=>{
            if (!isTracking) return;
            const offsetX = event.clientX - startPosition.x;
            const offsetY = event.clientY - startPosition.y;
            setOffset({
                x: offsetX,
                y: offsetY
            });
        };
        if (isTracking) {
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", mouseUp);
        } else {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", mouseUp);
        }
        return ()=>{
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", mouseUp);
        };
    }, [
        isTracking
    ]);
    return {
        offset,
        mouseDown
    };
};
